import { ReactNode, useEffect, useState, useRef, useMemo } from "react";
import { getDepartmentList } from "../redux/slices/employee";
import { dispatch, useSelector } from "../redux/store";
import {
  MoreHorizontal, Eye, Trash2, X, FileText, Download,
  ChevronLeft, ChevronRight, Save, Send, IndianRupee, Check, Filter as FilterIcon,
} from "lucide-react";
import { salaryApi, SalaryRecord, DailyBreakdown } from "../api/salary";
import FilterDropdown from "../components/FilterDropdown";
import ConfirmDialog, { type ConfirmState } from "../components/ConfirmDialog";
import api from "../api/api";
import { downloadSalarySlip, getMyEmployeeProfile, viewSalarySlip } from "../api/employees";
import { useAccess } from "../contexts/AccessContext";
import {
  buildApprovalStatusMap,
  getPreviousPayrollPeriod,
  statusAfterSendForApproval,
  successfulSendForApprovalIds,
} from "./payrollApprovalLogic";
import { toast } from "sonner";

type Employee = {
  id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  employeeID?: string;
  email?: string;
  hire_date?: string | null;
  profile_photo_url?: string | null;
  is_active?: boolean;
  employment_status?: string | null;
};

type PayrollRecordProps = {
  headerActions?: ReactNode;
  includeCurrentUserInEmployeeList?: boolean;
};

const ALL_DEPTS = "__ALL__";
const ALL_EMPS = "__ALL_EMP__";
const PAGE_SIZE = 10;
const PAYROLL_GREEN = "bg-[#0f2d1f] text-white hover:bg-[#123b29]";
const CALC_METHOD_ACTIVE = "border-[#0f2d1f] bg-[#0f2d1f] text-white shadow-sm hover:bg-[#123b29]";
const CALC_METHOD_IDLE = "border-[#b8c9bf] bg-[#e7f0eb] text-[#0f2d1f] hover:bg-[#dbe8df]";

// The three calculation methods a row can be computed with. Tracked per employee
// client-side so the "Calculation Method" badge reflects the last method applied.
type MethodKey = "attendance" | "noded" | "system";
const METHOD_META: Record<MethodKey, { label: string; cls: string }> = {
  attendance: { label: "Attendance", cls: "bg-indigo-100 text-indigo-700" },
  noded: { label: "No Deduction", cls: "bg-orange-100 text-orange-700" },
  system: { label: "Workflow", cls: "bg-emerald-100 text-emerald-700" },
};

// Salary approval lifecycle → Status badge. Derived from the record's flags:
// approved → Approved; else sent_for_approval → Sent for Approval; else Pending.
type StatusKey = "approved" | "sent" | "pending";
const STATUS_META: Record<StatusKey, { label: string; cls: string }> = {
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  sent: { label: "Sent for Approval", cls: "bg-blue-100 text-blue-700" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
};
const statusOf = (r: SalaryRecord): StatusKey =>
  r.approved ? "approved" : r.sent_for_approval ? "sent" : "pending";

// The bulk-calc endpoint returns a mix of real salary records and per-employee
// error objects ({ employee_id, success:false, error }) when a calc fails.
// Keep only genuine salary records so the table never renders blank ₹0 rows.
const filterOkResults = (results: any[]): SalaryRecord[] =>
  (results || []).filter((r) => r && r.success !== false && r.name) as SalaryRecord[];

const normaliseEmployeeList = (employees: Employee[], selfProfile: any): Employee[] => {
  if (!selfProfile?.has_employee_record || !selfProfile.id) return employees;
  if (employees.some((emp) => emp.id === selfProfile.id)) return employees;

  return [
    ...employees,
    {
      id: selfProfile.id,
      first_name: selfProfile.first_name || selfProfile.name || "Me",
      middle_name: selfProfile.middle_name,
      last_name: selfProfile.last_name || "",
      employeeID: selfProfile.employeeID,
      email: selfProfile.email,
      hire_date: selfProfile.hire_date,
      profile_photo_url: selfProfile.profile_photo_url,
      is_active: selfProfile.is_active,
      employment_status: selfProfile.employment_status,
    },
  ];
};

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toPayrollPeriodIndex = (year: string | number, month: string | number) =>
  Number(year) * 12 + Number(month);

const hireDateParts = (hireDate?: string | null) => {
  if (!hireDate) return null;
  const [year, month] = hireDate.split("-");
  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  if (!parsedYear || !parsedMonth) return null;
  return { year: parsedYear, month: parsedMonth };
};

const isBeforeJoiningMonth = (
  year: string | number,
  month: string | number,
  hireDate?: string | null
) => {
  const joining = hireDateParts(hireDate);
  if (!joining) return false;
  return toPayrollPeriodIndex(year, month) < toPayrollPeriodIndex(joining.year, joining.month);
};

const isJoinedForPayrollPeriod = (
  employee: Employee,
  year: string | number,
  month: string | number
) => !isBeforeJoiningMonth(year, month, employee.hire_date);

const payrollPeriodLabel = (year: string | number, month: string | number) =>
  `${MONTH_LABELS[Number(month) - 1] || month} ${year}`;

const joiningPeriodLabel = (hireDate?: string | null) => {
  const joining = hireDateParts(hireDate);
  return joining ? payrollPeriodLabel(joining.year, joining.month) : "the joining month";
};

const employeeDisplayName = (employee?: Employee) =>
  employee
    ? `${employee.first_name || ""}${employee.last_name ? " " + employee.last_name : ""}`.trim() ||
      employee.employeeID ||
      "This employee"
    : "This employee";

const employeeInitial = (name?: string) => (name || "?").trim().charAt(0).toUpperCase() || "?";

const isActiveEmployee = (employee: Employee) => {
  const employmentStatus = (employee.employment_status || "active").toLowerCase();
  return employee.is_active !== false && employmentStatus === "active";
};

const activeEmployeesOnly = (employees: Employee[]) => employees.filter(isActiveEmployee);

const PayrollLoadingState = ({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) => (
  <div
    role="status"
    aria-label={label}
    className={`flex flex-col items-center justify-center text-center ${
      compact ? "gap-3 px-6 py-8" : "gap-5 px-6 py-12"
    }`}
  >
    <div className="relative h-16 w-16">
      <div className="absolute inset-0 rounded-2xl bg-[#0f2d1f]/10" />
      <div className="absolute inset-2 rounded-xl bg-white shadow-sm" />
      <div className="absolute inset-0 rounded-2xl border border-[#0f2d1f]/15" />
      <div className="absolute left-1/2 top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#0f2d1f]/15 border-t-[#0f2d1f] animate-spin" />
      <IndianRupee className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-[#0f2d1f]" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      <div className="mt-3 flex w-48 items-center gap-2">
        <span className="h-1.5 flex-1 rounded-full bg-[#0f2d1f]/20 animate-pulse" />
        <span className="h-1.5 flex-[1.4] rounded-full bg-[#0f2d1f]/30 animate-pulse [animation-delay:150ms]" />
        <span className="h-1.5 flex-[0.8] rounded-full bg-[#0f2d1f]/20 animate-pulse [animation-delay:300ms]" />
      </div>
    </div>
  </div>
);

const EmployeeAvatar = ({
  name,
  photoUrl,
}: {
  name?: string;
  photoUrl?: string | null;
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showPhoto = Boolean(photoUrl) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl]);

  return (
    <span
      aria-hidden="true"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-xs font-semibold text-gray-700"
    >
      {showPhoto ? (
        <img
          src={photoUrl || ""}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        employeeInitial(name)
      )}
    </span>
  );
};

const CircleCheckbox = ({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={disabled ? undefined : onChange}
    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
      disabled
        ? "cursor-not-allowed border-gray-200 bg-gray-100"
        : checked
        ? "border-[#0f2d1f] bg-[#0f2d1f]"
        : "border-gray-400 bg-white hover:border-[#0f2d1f]"
    }`}
  >
    {checked && <Check className={`h-3 w-3 ${disabled ? "text-gray-300" : "text-white"}`} />}
  </button>
);

const PayrollRecord = ({
  headerActions,
  includeCurrentUserInEmployeeList = false,
}: PayrollRecordProps = {}) => {
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  // Manage level gates payroll mutations (switch/revert/adjust/delete/send); View → read-only.
  const { hasAccess, canManage } = useAccess();
  const canPayrollManage = !hasAccess || canManage("payroll-records");

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);

  const defaultPayrollPeriod = useMemo(() => getPreviousPayrollPeriod(), []);
  const [selectedYear, setSelectedYear] = useState(defaultPayrollPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(defaultPayrollPeriod.month);
  const [selectedDept, setSelectedDept] = useState<string>(ALL_DEPTS);
  const [selectedEmp, setSelectedEmp] = useState<string>(ALL_EMPS);
  const [draftYear, setDraftYear] = useState(defaultPayrollPeriod.year);
  const [draftMonth, setDraftMonth] = useState(defaultPayrollPeriod.month);
  const [draftDept, setDraftDept] = useState<string>(ALL_DEPTS);
  const [draftEmp, setDraftEmp] = useState<string>(ALL_EMPS);

  const [isLoading, setIsLoading] = useState(false);
  const [salaryRecords, setSalaryRecords] = useState<SalaryRecord[]>([]);
  // Employee ids (record.employee_id) currently selected via row checkboxes.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // employee_id -> calculation method last applied to that row.
  const [methodById, setMethodById] = useState<Record<string, MethodKey>>({});
  // employee_id -> authoritative approval status (from get_approved_salary, which
  // returns only sent/approved rows). Overlays the per-row draft flags so the
  // Status column can show "Approved" even after the page regenerates a draft.
  const [statusById, setStatusById] = useState<Record<string, StatusKey>>({});
  // employee_id -> amount already sitting in the approval queue (from get_approved_salary).
  const [pendingById, setPendingById] = useState<Record<string, number>>({});
  // employee_id -> the sent/approved SNAPSHOT figures. `net` already includes
  // bonus + extra pay. Overlaid onto the table so an approved employee's row
  // shows the bonus / extra that were actually approved instead of a freshly
  // recalculated draft (which resets them to 0).
  const [approvedFiguresById, setApprovedFiguresById] = useState<
    Record<string, { net: number; bonus: number; extrapay: number }>
  >({});
  const [activeMethod, setActiveMethod] = useState<MethodKey>("attendance");
  // Re-send warning: employees whose salary is ALREADY pending approval.
  const [resendConflicts, setResendConflicts] = useState<
    { id: string; name: string; pending: number; current: number }[]
  >([]);

  const [dailyData, setDailyData] = useState<DailyBreakdown[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SalaryRecord | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [adjustmentData, setAdjustmentData] = useState({
    bonus: 0,
    extrapay: 0,
    description: "",
  });
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [page, setPage] = useState(1);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Build the year list around the current year so the default year is always selectable.
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));

  const monthLabel = (m: string) => months.find((mm) => mm.value === m)?.label || m;

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  // Close row menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);
        const response = await api.get("/employeesAll", {
          params: { active_only: true },
        });
        let nextEmployees = activeEmployeesOnly(response.data.employees || []);

        if (includeCurrentUserInEmployeeList) {
          try {
            const selfProfile = await getMyEmployeeProfile();
            nextEmployees = activeEmployeesOnly(normaliseEmployeeList(nextEmployees, selfProfile));
          } catch {
            /* Self row is best-effort; keep the role-scoped employee list. */
          }
        }

        setEmployees(nextEmployees);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, [includeCurrentUserInEmployeeList]);

  // Format Department name
  const formatDepartment = (dept?: string): string => {
    if (!dept) return "General";
    return dept
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Merge freshly-calculated records into salaryRecords by employee_id (keep others).
  const mergeRecords = (incoming: SalaryRecord[]) => {
    setSalaryRecords((prev) => {
      const byId = new Map<string, SalaryRecord>();
      prev.forEach((r) => byId.set(r.employee_id, r));
      incoming.forEach((r) => byId.set(r.employee_id, r));
      return Array.from(byId.values());
    });
  };

  // AUTO-RUN attendance calc for ALL employees once they load and whenever the period changes.
  useEffect(() => {
    if (employeesLoading || employees.length === 0) return;
    const allIds = employees
      .filter((employee) => isJoinedForPayrollPeriod(employee, selectedYear, selectedMonth))
      .map((e) => e.id);
    runBulk(selectedYear, selectedMonth, allIds, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesLoading, employees, selectedYear, selectedMonth]);

  // Pull the authoritative approval status per employee for the period.
  // get_approved_salary returns only sent/approved rows, so an approved salary
  // stays "Approved" even after the page regenerates a fresh draft.
  const refreshStatuses = async (year: string, month: string) => {
    try {
      const res = await salaryApi.getApprovedSalaries(year, month);
      const map = buildApprovalStatusMap((res.data?.results as any[]) || []);
      const amounts: Record<string, number> = {};
      const figures: Record<string, { net: number; bonus: number; extrapay: number }> = {};
      ((res.data?.results as any[]) || []).forEach((r) => {
        const d = r?.data || r;
        if (d?.emptID) {
          amounts[d.emptID] =
            (d.netSalary || 0) + (d.bonus || 0) + (d.extrapay || 0);
          // Snapshot netSalary already includes bonus + extra pay.
          figures[d.emptID] = {
            net: d.netSalary || 0,
            bonus: d.bonus || 0,
            extrapay: d.extrapay || 0,
          };
        }
      });
      setStatusById(map);
      setPendingById(amounts);
      setApprovedFiguresById(figures);
    } catch {
      /* best-effort; fall back to per-row flags */
    }
  };

  // Run attendance-based bulk calc; replace records + select all + clear no-deduction flags.
  const runBulk = async (year: string, month: string, ids: string[], selectAll: boolean) => {
    if (!ids.length) {
      setSalaryRecords([]);
      setSelectedIds(new Set());
      setMethodById({});
      setStatusById({});
      setPendingById({});
      setApprovedFiguresById({});
      setActiveMethod("attendance");
      setPage(1);
      return;
    }
    setIsLoading(true);
    try {
      const response = await salaryApi.calculateBulk(year, month, ids);
      const records: SalaryRecord[] = filterOkResults(response.data.results);
      setSalaryRecords(records);
      setMethodById(Object.fromEntries(records.map((r) => [r.employee_id, "attendance" as MethodKey])));
      setActiveMethod("attendance");
      if (selectAll) {
        setSelectedIds(new Set(records.map((r) => r.employee_id)));
      }
      setPage(1);
      refreshStatuses(year, month);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || "Failed to calculate salary";
      toast.error(errorMsg);
      setSalaryRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Pick the calc endpoint for a given method.
  const calcApiFor = (method: MethodKey, ids: string[]) => {
    if (method === "noded")
      return salaryApi.calculateNoDeduction(selectedYear, selectedMonth, ids);
    if (method === "system")
      return salaryApi.calculateWithSystemTracking(selectedYear, selectedMonth, ids);
    return salaryApi.calculateBulk(selectedYear, selectedMonth, ids);
  };

  const setMethodFor = (ids: string[], method: MethodKey) =>
    setMethodById((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = method;
      });
      return next;
    });

  // Toolbar: recalculate the SELECTED rows with one of the three methods
  // (attendance / no-deduction / system-level) and merge the results back.
  const recalcSelected = async (method: MethodKey) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setIsLoading(true);
    try {
      const response = await calcApiFor(method, ids);
      const records: SalaryRecord[] = filterOkResults(response.data.results);
      mergeRecords(records);
      setMethodFor(records.map((r) => r.employee_id), method);
      setActiveMethod(method);
      toast.success(`Recalculated ${records.length} employee(s) using ${METHOD_META[method].label}.`);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Recalculation failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Toolbar: Send selected rows for approval (bell notification to each employee).
  // Actually send: notify employees + mark sent. Used directly and after the
  // "Update to Latest" confirmation in the re-send warning.
  const doSendForApproval = async (ids: string[]) => {
    if (!ids.length) return;
    setResendConflicts([]);
    setIsLoading(true);
    try {
      const response = await salaryApi.sendForApproval(selectedYear, selectedMonth, ids);
      const successfulIds = successfulSendForApprovalIds(response, ids);
      const sent = response?.data?.sent_count ?? successfulIds.length;
      // Reflect the new state in the Status column immediately.
      const sentSet = new Set(successfulIds);
      setSalaryRecords((prev) =>
        prev.map((r) =>
          sentSet.has(r.employee_id) ? { ...r, approved: false, sent_for_approval: true } : r
        )
      );
      setStatusById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = statusAfterSendForApproval(next[id]);
        });
        return next;
      });
      toast.success(
        `Salary for ${monthLabel(selectedMonth)} ${selectedYear} sent for approval — ${sent} employee(s) notified.`
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Failed to send for approval");
    } finally {
      setIsLoading(false);
    }
  };

  // Send button: if any selected salary is ALREADY pending approval, warn first
  // (show what's already queued) and let the admin cancel or update to latest.
  const statusForEmployeeId = (id: string): StatusKey => {
    const record = salaryRecords.find((r) => r.employee_id === id);
    return statusById[id] ?? (record ? statusOf(record) : "pending");
  };

  const sendableApprovalIds = (ids: string[]) =>
    ids.filter((id) => statusForEmployeeId(id) !== "approved");

  const handleSendForApproval = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const sendableIds = sendableApprovalIds(ids);
    if (!sendableIds.length) {
      toast.error("Approved salaries cannot be sent for approval again.");
      return;
    }
    const conflictIds = sendableIds.filter((id) => statusForEmployeeId(id) === "sent");
    if (conflictIds.length) {
      setResendConflicts(
        conflictIds.map((id) => {
          const rec = salaryRecords.find((r) => r.employee_id === id);
          const current = rec
            ? (rec.netSalary || 0) + (rec.bonus || 0) + (rec.extrapay || 0)
            : 0;
          return { id, name: rec?.name || id, pending: pendingById[id] ?? current, current };
        })
      );
      return; // modal opens off resendConflicts
    }
    doSendForApproval(sendableIds);
  };

  const handleApplyFilters = () => {
    setSelectedDept(draftDept);
    setSelectedEmp(draftEmp);
    setSelectedYear(draftYear);
    setSelectedMonth(draftMonth);
    setPage(1);
  };

  // Manual Salary Adjustment Handler
  const handleAdjustSalary = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setAdjustmentData({
      bonus: record.bonus || 0,
      extrapay: record.extrapay || 0,
      description: "",
    });
    setShowAdjustDialog(true);
    setOpenMenuId(null);
  };

  // Submit Salary Adjustment
  const handleSubmitAdjustment = async () => {
    if (!selectedRecord) return;

    // An already-approved salary is locked — never let an adjustment through.
    const currentStatus = statusById[selectedRecord.employee_id] ?? statusOf(selectedRecord);
    if (currentStatus === "approved") {
      toast.error("This salary is already approved and can’t be adjusted.");
      return;
    }

    setIsAdjusting(true);
    try {
      await salaryApi.manualSalaryAdjustment({
        employee_id: selectedRecord.employee_id,
        bonus: adjustmentData.bonus,
        extrapay: adjustmentData.extrapay,
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
        description: adjustmentData.description || "Manual adjustment",
      });

      toast.success(`Salary adjustment applied successfully for ${selectedRecord.name}!`);
      setShowAdjustDialog(false);

      // Recalculate this employee (using whichever method its row currently
      // uses) to pull the updated bonus / extra pay and merge back.
      const method =
        methodById[selectedRecord.employee_id] ||
        (selectedRecord.status === "Manually" ? "noded" : "attendance");
      const response = await calcApiFor(method, [selectedRecord.employee_id]);
      mergeRecords(filterOkResults(response.data.results));

      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
      setSelectedRecord(null);
    } catch (error: any) {
      console.error("Failed to adjust salary:", error);
      toast.error(error?.response?.data?.detail || "Failed to adjust salary");
    } finally {
      setIsAdjusting(false);
    }
  };

  // Fetch daily breakdown data only when the details modal is opened.
  const handleViewMore = async (record: SalaryRecord) => {
    setDailyData([]);
    setSelectedRecord(record);
    setShowDialog(true);
    setOpenMenuId(null);
    setDetailsLoading(true);

    try {
      const response = await salaryApi.getCalculationLogs(record.salary_id);
      setDailyData(response.data?.attendance || []);
    } catch (error: any) {
      console.error("Failed to fetch salary calculation logs:", error);
      toast.error(error?.response?.data?.detail || "Failed to load attendance details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePreviewSalarySlip = async (record: SalaryRecord) => {
    try {
      await viewSalarySlip(record.employee_id, Number(record.month), Number(record.year));
    } catch (error: any) {
      toast.error(error?.message || "Failed to open salary-slip PDF");
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleDownloadSalarySlip = async (record: SalaryRecord) => {
    try {
      await downloadSalarySlip(record.employee_id, Number(record.month), Number(record.year));
    } catch (error: any) {
      toast.error(error?.message || "Failed to download salary-slip PDF");
    } finally {
      setOpenMenuId(null);
    }
  };

  // Delete handler (client-side removal from the current record set)
  const handleDelete = (record: SalaryRecord) => {
    setConfirm({
      title: `Delete salary record for ${record.name}?`,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        setSalaryRecords((prev) => prev.filter((r) => r.employee_id !== record.employee_id));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(record.employee_id);
          return next;
        });
        toast.success("Salary record removed from view.");
        setOpenMenuId(null);
      },
    });
  };

  // Department + Employee filters apply to which rows are DISPLAYED (client-side).
  const filteredRecords = useMemo(() => {
    return salaryRecords.filter(
      (r) =>
        (selectedDept === ALL_DEPTS || r.department === selectedDept) &&
        (selectedEmp === ALL_EMPS || r.employee_id === selectedEmp)
    );
  }, [salaryRecords, selectedDept, selectedEmp]);

  // Reset to page 1 whenever the filtered set changes shape.
  useEffect(() => {
    setPage(1);
  }, [selectedDept, selectedEmp, salaryRecords.length]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRecords = filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Selection helpers operate on the filtered set (across pages), EXCLUDING
  // approved rows — those are locked, so they can't be selected and don't count
  // toward "Select All" or the selected tally.
  const selectableIds = useMemo(
    () =>
      filteredRecords
        .filter((r) => (statusById[r.employee_id] ?? statusOf(r)) !== "approved")
        .map((r) => r.employee_id),
    [filteredRecords, statusById]
  );
  const allFilteredSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const employeePhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    employees.forEach((employee) => {
      if (employee.profile_photo_url) {
        map[employee.id] = employee.profile_photo_url;
      }
    });
    return map;
  }, [employees]);

  const toggleRow = (employeeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Method last applied to a row (defaults from the record status for rows
  // that were computed before any explicit method toggle).
  const methodOf = (record: SalaryRecord): MethodKey =>
    methodById[record.employee_id] || (record.status === "Manually" ? "noded" : "attendance");

  // Count only selectable (non-approved) rows that are selected, so the tally
  // matches what "Select All" can actually pick and never includes a locked row.
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  const mutationsDisabled = isLoading || selectedCount === 0;
  // Adjustment dialog: an already-approved salary is locked. Surface the approved
  // (set) amount from the approval queue and disable Apply so it can't be
  // re-adjusted from here.
  const adjustStatus: StatusKey = selectedRecord
    ? statusById[selectedRecord.employee_id] ?? statusOf(selectedRecord)
    : "pending";
  const isAdjustApproved = adjustStatus === "approved";
  // Approval snapshot for the selected employee (net already includes bonus +
  // extra). Prefer it so the dialog shows the bonus / extra pay that were
  // actually approved, consistent with the Payroll Records table.
  const adjustSnapshot = selectedRecord
    ? approvedFiguresById[selectedRecord.employee_id]
    : undefined;
  // Bonus / extra shown in the dialog: the approved snapshot for locked rows,
  // the live input values while editing.
  const adjustShownBonus = isAdjustApproved ? (adjustSnapshot?.bonus ?? 0) : adjustmentData.bonus;
  const adjustShownExtra = isAdjustApproved ? (adjustSnapshot?.extrapay ?? 0) : adjustmentData.extrapay;
  // Calculated (base) net BEFORE bonus/extra. The stored net already bakes them
  // in, so back them out for the "Net Salary" line — otherwise "New Net Salary"
  // (base + bonus + extra) would count bonus/extra twice. Mirrors the backend,
  // which recomputes new net as (netSalary − currentBonus − currentExtra) + new.
  const adjustBaseNet = adjustSnapshot
    ? adjustSnapshot.net - adjustSnapshot.bonus - adjustSnapshot.extrapay
    : (selectedRecord?.netSalary || 0) - (selectedRecord?.bonus || 0) - (selectedRecord?.extrapay || 0);
  const adjustNewNet = adjustBaseNet + adjustShownBonus + adjustShownExtra;
  const selectedEmployeeRecord = employees.find((employee) => employee.id === selectedEmp);
  const selectedEmployeeNotJoined =
    selectedEmp !== ALL_EMPS &&
    isBeforeJoiningMonth(selectedYear, selectedMonth, selectedEmployeeRecord?.hire_date);
  const employeesBeforeJoinCount = employees.filter((employee) =>
    isBeforeJoiningMonth(selectedYear, selectedMonth, employee.hire_date)
  ).length;
  const emptyTitle = selectedEmployeeNotJoined ? "Not joined for this period" : "No Salary Records";
  const emptyMessage = selectedEmployeeNotJoined
    ? `${employeeDisplayName(selectedEmployeeRecord)} had not joined the organisation for ${payrollPeriodLabel(
        selectedYear,
        selectedMonth
      )}. Payroll records are available from ${joiningPeriodLabel(
        selectedEmployeeRecord?.hire_date
      )} onward.`
    : isLoading
    ? "Calculating salary..."
    : employees.length > 0 && employeesBeforeJoinCount === employees.length
    ? `No employees had joined the organisation for ${payrollPeriodLabel(selectedYear, selectedMonth)}.`
    : "Change the filters to refresh salary records.";
  const calculationButtonClass = (method: MethodKey) =>
    `px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
      activeMethod === method ? CALC_METHOD_ACTIVE : CALC_METHOD_IDLE
    } disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed`;
  const HeaderLabel = ({
    first,
    second,
    align = "left",
    testId,
  }: {
    first: string;
    second?: string;
    align?: "left" | "right";
    testId?: string;
  }) => (
    <span
      data-testid={testId}
      aria-label={`${first}${second ? ` ${second}` : ""}`}
      className={`inline-flex flex-col leading-tight ${
        align === "right" ? "items-end text-right" : "items-start"
      }`}
    >
      <span>{first}</span>
      {second && <span>{second}</span>}
    </span>
  );

  if (employeesLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white shadow rounded-2xl max-w-md w-full">
          <PayrollLoadingState label="Loading active employees" />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="payroll-record-page"
      className="min-h-[calc(100vh-64px)] bg-gray-50 p-4"
    >
      <div
        data-testid="payroll-record-card"
        className="mx-auto flex w-full max-w-[96rem] flex-col overflow-hidden rounded-2xl bg-white shadow"
      >
          <div
            data-testid="payroll-record-controls"
            className="shrink-0 rounded-t-2xl bg-white shadow-[0_10px_28px_-24px_rgba(15,45,31,0.65)]"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-200">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="text-xl font-bold text-gray-800 mb-1">Payroll Records</h1>
                  <p className="text-sm text-gray-500 max-w-3xl">
                    Salary is calculated based on daily attendance records, including present days,
                    absences, overtime hours, and applicable deductions. This method uses real-time
                    attendance data from the system.
                  </p>
                </div>
                {headerActions && (
                  <div className="shrink-0 sm:pt-1">
                    {headerActions}
                  </div>
                )}
              </div>
            </div>

            {/* Filter row */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col w-56">
                  <label htmlFor="payroll-record-department" className="text-xs font-semibold text-gray-600 mb-1">Department</label>
                  <FilterDropdown
                    value={draftDept}
                    onChange={setDraftDept}
                    ariaLabel="Filter by department"
                    options={[
                      { value: ALL_DEPTS, label: "All Departments" },
                      ...departmentList.map((dept: any) => ({ value: String(dept.id), label: formatDepartment(dept.name) })),
                    ]}
                  />
                </div>

                <div className="flex flex-col w-56">
                  <label htmlFor="payroll-record-employee" className="text-xs font-semibold text-gray-600 mb-1">Employee</label>
                  <FilterDropdown
                    value={draftEmp}
                    onChange={setDraftEmp}
                    ariaLabel="Filter by employee"
                    options={[
                      { value: ALL_EMPS, label: "All Employees" },
                      ...employees.map((emp) => ({ value: String(emp.id), label: `${emp.first_name}${emp.last_name ? " " + emp.last_name : ""}${emp.employeeID ? ` (${emp.employeeID})` : ""}` })),
                    ]}
                  />
                </div>

                <div className="flex flex-col w-24">
                  <label htmlFor="payroll-record-year" className="text-xs font-semibold text-gray-600 mb-1">Year</label>
                  <FilterDropdown value={draftYear} onChange={setDraftYear} searchable={false} ariaLabel="Filter by year" options={years} />
                </div>

                <div className="flex flex-col w-32">
                  <label htmlFor="payroll-record-month" className="text-xs font-semibold text-gray-600 mb-1">Month</label>
                  <FilterDropdown value={draftMonth} onChange={setDraftMonth} searchable={false} ariaLabel="Filter by month" options={months} />
                </div>

                <button
                  type="button"
                  onClick={handleApplyFilters}
                  disabled={isLoading || employeesLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0f2d1f] text-white text-sm font-semibold rounded-lg hover:bg-[#123b29] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <FilterIcon className="w-4 h-4" />
                  Apply
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-white">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSelectAll}
                  disabled={filteredRecords.length === 0}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
                <span className="text-xs text-gray-500">
                  {selectedCount} selected · {filteredRecords.length} shown
                </span>
              </div>

              {canPayrollManage && (
                <div className="flex flex-wrap items-center gap-2">
                  {/* Recalculate the selected rows with one of the three methods. */}
                  <button
                    type="button"
                    onClick={() => recalcSelected("attendance")}
                    disabled={mutationsDisabled}
                    aria-pressed={activeMethod === "attendance"}
                    className={calculationButtonClass("attendance")}
                  >
                    Attendance
                  </button>
                  <button
                    type="button"
                    onClick={() => recalcSelected("system")}
                    disabled={mutationsDisabled}
                    aria-pressed={activeMethod === "system"}
                    className={calculationButtonClass("system")}
                  >
                    Workflow
                  </button>
                  <button
                    type="button"
                    onClick={() => recalcSelected("noded")}
                    disabled={mutationsDisabled}
                    aria-pressed={activeMethod === "noded"}
                    className={calculationButtonClass("noded")}
                  >
                    No Deduction
                  </button>
                  <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />
                  <button
                    onClick={handleSendForApproval}
                    disabled={mutationsDisabled}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${PAYROLL_GREEN} disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5`}
                  >
                    <Send className="w-3.5 h-3.5" />
                    Send for Approval
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            data-testid="payroll-table-section"
            className="flex flex-col p-4"
          >
            {isLoading && filteredRecords.length === 0 ? (
              <PayrollLoadingState label="Calculating payroll records" />
            ) : filteredRecords.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <IndianRupee className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-500 mb-1">{emptyTitle}</h3>
                <p className="text-sm text-gray-400">
                  {emptyMessage}
                </p>
              </div>
            ) : (
              <>
                <div
                  data-testid="payroll-table-scroll"
                  className="relative overflow-auto rounded-lg border border-gray-200 bg-white"
                  style={{ maxHeight: "calc(100vh - 140px)" }}
                >
                {isLoading && (
                  <div className="absolute inset-0 z-30 flex items-start justify-center bg-white/80 backdrop-blur-[2px]">
                    <PayrollLoadingState label="Calculating payroll records" compact />
                  </div>
                )}
                <table className="pr-table w-full min-w-[1180px] table-auto">
                  <colgroup>
                    <col style={{ width: "36px" }} />
                    <col style={{ width: "200px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "86px" }} />
                    <col style={{ width: "92px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "166px" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200 text-left">
                      <th className="sticky left-0 top-0 z-30 bg-gray-100 px-3 py-2" />
                      <th className="sticky left-10 top-0 z-30 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide shadow-[8px_0_12px_-12px_rgba(15,23,42,0.45)]">Name</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">Department</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="TakeHome" second="Salary" align="right" testId="payroll-header-takehome-salary" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Calculated" second="Salary" align="right" testId="payroll-header-calculated-salary" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">Bonus</th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Extra" second="Pay" align="right" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide text-right">
                        <HeaderLabel first="Total" second="Salary" align="right" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                        <HeaderLabel first="Calculation" second="Method" />
                      </th>
                      <th className="sticky top-0 z-20 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {pageRecords.map((record) => {
                      const isMenuOpen = openMenuId === record.employee_id;
                      const isSelected = selectedIds.has(record.employee_id);
                      const methodMeta = METHOD_META[methodOf(record)];
                      const rowStatus = statusById[record.employee_id] ?? statusOf(record);
                      const statusMeta = STATUS_META[rowStatus];
                      // An approved salary is locked: the row can't be selected or
                      // edited/deleted — only viewed (see the gated menu below).
                      const isApproved = rowStatus === "approved";
                      const deptName = formatDepartment(
                        departmentList.find((e: any) => e.id === record.department)?.name
                      );
                      // Employee's actual monthly salary (gross) — matches the
                      // employee Payroll page. Avoids the declared-net takeHome
                      // which can go negative (flat LWF) on zero-salary rows.
                      const monthly = record.takeHome || 0;
                      // For sent/approved employees, show the figures from the
                      // approval snapshot (which carry the bonus / extra pay that
                      // was actually approved) instead of the freshly recalculated
                      // draft, which resets them to 0. Snapshot `net` already
                      // includes bonus + extra, so back them out for the
                      // Calculated column to keep Calculated + Bonus + Extra = Total.
                      const approvedFig = approvedFiguresById[record.employee_id];
                      const bonus = approvedFig ? approvedFig.bonus : (record.bonus || 0);
                      const extra = approvedFig ? approvedFig.extrapay : (record.extrapay || 0);
                      const calc = approvedFig
                        ? approvedFig.net - bonus - extra
                        : record.netSalary || 0;
                      const total = approvedFig ? approvedFig.net : calc + bonus + extra;
                      const profilePhotoUrl =
                        record.profile_photo_url || employeePhotoById[record.employee_id] || null;

                      return (
                        <tr
                          key={record.employee_id}
                          className={`group transition-colors ${
                            isApproved ? "bg-gray-50/60 opacity-60" : "hover:bg-gray-50"
                          }`}
                          title={isApproved ? "Approved — locked (view only)" : undefined}
                        >
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 group-hover:bg-gray-50">
                            <CircleCheckbox
                              checked={isSelected}
                              onChange={() => toggleRow(record.employee_id)}
                              label={`Select ${record.name}`}
                              disabled={isApproved}
                            />
                          </td>
                          <td className="sticky left-10 z-10 bg-white px-3 py-2 shadow-[8px_0_12px_-12px_rgba(15,23,42,0.35)] group-hover:bg-gray-50" title={`${record.name}${record.employeeID ? ` (${record.employeeID})` : ""}`}>
                            <div
                              className="flex min-w-0 items-center gap-2"
                              aria-label={`Employee ${record.name}, ID ${record.employeeID || "not available"}`}
                            >
                              <EmployeeAvatar name={record.name} photoUrl={profilePhotoUrl} />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold leading-tight text-gray-900">{record.name}</span>
                                <span className="block truncate text-xs font-medium text-gray-400">{record.employeeID}</span>
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 truncate" title={deptName}>
                            {deptName}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-800 text-right whitespace-nowrap">
                            ₹{monthly.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-800 text-right whitespace-nowrap">
                            ₹{calc.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-amber-600 text-right whitespace-nowrap">
                            ₹{bonus.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-blue-600 text-right whitespace-nowrap">
                            ₹{extra.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs font-bold text-gray-900 text-right whitespace-nowrap">
                            ₹{total.toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex max-w-[108px] whitespace-normal break-words px-2 py-0.5 text-xs font-semibold leading-tight rounded-full ${methodMeta.cls}`}
                            >
                              {methodMeta.label}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            <div className="relative flex items-center justify-start gap-1.5 whitespace-nowrap" ref={isMenuOpen ? menuRef : undefined}>
                              <span
                                className={`inline-flex whitespace-nowrap px-2 py-0.5 text-xs font-semibold leading-tight rounded-full ${statusMeta.cls}`}
                              >
                                {statusMeta.label}
                              </span>
                              {isApproved ? (
                                <>
                                  {/* Approved salary is locked → direct read-only actions, no three-dot menu. */}
                                  <button
                                    type="button"
                                    aria-label={`View details for ${record.name}`}
                                    title="View details"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewMore(record);
                                    }}
                                    className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                  >
                                    <Eye className="w-4 h-4 text-gray-500" />
                                  </button>
                                  {canPayrollManage && (
                                    <>
                                      <button
                                        type="button"
                                        aria-label={`View Salary Slip PDF for ${record.name}`}
                                        title="View Salary Slip PDF"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreviewSalarySlip(record);
                                        }}
                                        className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                      >
                                        <FileText className="w-4 h-4 text-gray-500" />
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={`Download Salary Slip PDF for ${record.name}`}
                                        title="Download Salary Slip PDF"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadSalarySlip(record);
                                        }}
                                        className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                      >
                                        <Download className="w-4 h-4 text-gray-500" />
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    aria-label={`Actions for ${record.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(isMenuOpen ? null : record.employee_id);
                                    }}
                                    className="shrink-0 p-1 rounded hover:bg-gray-100 transition-all"
                                  >
                                    <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                  </button>

                                  {isMenuOpen && (
                                    <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewMore(record);
                                        }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        View Details
                                      </button>
                                      {canPayrollManage && (
                                        <>
                                          <button
                                            type="button"
                                            aria-label={`View Salary Slip PDF for ${record.name}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePreviewSalarySlip(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs border-t border-gray-100"
                                          >
                                            <FileText className="w-3.5 h-3.5" />
                                            View Salary Slip PDF
                                          </button>
                                          <button
                                            type="button"
                                            aria-label={`Download Salary Slip PDF for ${record.name}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadSalarySlip(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-xs border-t border-gray-100"
                                          >
                                            <Download className="w-3.5 h-3.5" />
                                            Download Salary Slip PDF
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAdjustSalary(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-gray-700 hover:text-blue-600 text-xs border-t border-gray-100"
                                          >
                                            <Save className="w-3.5 h-3.5" />
                                            Adjust Salary
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(record);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-gray-700 hover:text-red-600 text-xs border-t border-gray-100"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex shrink-0 items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {filteredRecords.length} record(s) total
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-medium text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
      </div>

      {/* Dialog Modal for Attendance Breakdown */}
      {showDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ color: "#fff" }}>Daily Attendance Breakdown</h2>
                  <p className="text-xs text-emerald-100">
                    {selectedRecord.name} -{" "}
                    {months.find(
                      (m) => m.value === selectedRecord.month?.toString().padStart(2, "0")
                    )?.label}{" "}
                    {selectedRecord.year}
                  </p>
                </div>
                <button
                  onClick={() => setShowDialog(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {detailsLoading ? (
                <div className="text-center py-8" role="status">
                  <p className="text-gray-500 text-sm">Loading attendance details...</p>
                </div>
              ) : dailyData.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No attendance data available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[500px]">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Hours</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Payable</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Overtime</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Day Salary</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">OT Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dailyData.map((day, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                            {new Date(day.date).toLocaleDateString("en-IN")}
                          </td>
                          <td className="px-3 py-2 text-gray-700 font-mono">{day.hours}h</td>
                          <td className="px-3 py-2">
                            {(() => {
                              // Show "OVERTIME" when the day has any overtime,
                              // otherwise the raw status (FULL_DAY / ABSENT / …).
                              const hasOt = (day.overtime || 0) > 0;
                              const label = hasOt ? "OVERTIME" : day.status;
                              const cls = hasOt
                                ? "bg-purple-100 text-purple-700"
                                : day.status === "ABSENT"
                                ? "bg-red-100 text-red-700"
                                : day.status === "PRESENT"
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700";
                              return (
                                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                          {/* Payable is a DAY count (1 / 0.5 / 0), not hours. */}
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{day.payable}</td>
                          <td className="px-3 py-2 text-right font-mono text-purple-600">{day.overtime}h</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-blue-600">
                            ₹{day.day_salary?.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">
                            ₹{day.overtime_amount?.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700 text-sm">Total Net Salary:</span>
                <span className="text-xl font-bold text-emerald-600">
                  ₹
                  {dailyData
                    .reduce(
                      (acc, day) => acc + (day.day_salary || 0) + (day.overtime_amount || 0),
                      0
                    )
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Salary Adjustment Dialog */}
      {showAdjustDialog && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ color: "#fff" }}>Manual Salary Adjustment</h2>
                  <p className="text-xs text-blue-100">
                    {selectedRecord.name} -{" "}
                    {months.find(
                      (m) => m.value === selectedRecord.month?.toString().padStart(2, "0")
                    )?.label}{" "}
                    {selectedRecord.year}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAdjustDialog(false);
                    setSelectedRecord(null);
                    setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                  }}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="space-y-3">
                {/* Bonus Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-amber-600" />
                    Bonus Amount
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.bonus || ""}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, bonus: parseInt(e.target.value) || 0 })
                    }
                    min="0"
                    step="100"
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter bonus amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Additional bonus to be added to salary</p>
                </div>

                {/* Extra Pay Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-blue-600" />
                    Extra Pay
                  </label>
                  <input
                    type="number"
                    value={adjustmentData.extrapay || ""}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, extrapay: parseInt(e.target.value) || 0 })
                    }
                    min="0"
                    step="100"
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter extra pay amount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Extra payment to be added to salary</p>
                </div>

                {/* Description Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    Description (Optional)
                  </label>
                  <textarea
                    value={adjustmentData.description}
                    onChange={(e) =>
                      setAdjustmentData({ ...adjustmentData, description: e.target.value })
                    }
                    rows={2}
                    disabled={isAdjustApproved}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm resize-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                    placeholder="Enter reason for adjustment..."
                  />
                </div>

                {/* Current Salary Info */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {isAdjustApproved ? "Approved Net Salary:" : "Current Net Salary:"}
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₹{adjustBaseNet?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Bonus:</span>
                    <span className="font-medium text-amber-600">
                      ₹{adjustShownBonus?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Extra Pay:</span>
                    <span className="font-medium text-blue-600">
                      ₹{adjustShownExtra?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">New Net Salary:</span>
                    <span className="font-bold text-emerald-600">
                      ₹{adjustNewNet?.toLocaleString()}
                    </span>
                  </div>
                </div>

                {isAdjustApproved && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                    This salary is already approved and can’t be adjusted.
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdjustDialog(false);
                      setSelectedRecord(null);
                      setAdjustmentData({ bonus: 0, extrapay: 0, description: "" });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitAdjustment}
                    disabled={isAdjusting || isAdjustApproved}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    {isAdjusting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Applying...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Apply Adjustment</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Re-send warning: some selected salaries are already pending approval. */}
      {resendConflicts.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-amber-500 px-5 py-3">
              <h2 className="text-lg font-bold" style={{ color: "#fff" }}>
                Already sent for approval
              </h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-3">
                {resendConflicts.length} salar{resendConflicts.length === 1 ? "y is" : "ies are"} already
                pending approval. Sending again will update {resendConflicts.length === 1 ? "it" : "them"} with
                the latest calculation.
              </p>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg mb-4">
                {resendConflicts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm gap-3">
                    <span className="font-medium text-gray-800 truncate">{c.name}</span>
                    <span className="text-gray-600 whitespace-nowrap">
                      Pending ₹{c.pending.toLocaleString()}
                      {c.current !== c.pending && (
                        <> → Latest ₹{c.current.toLocaleString()}</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setResendConflicts([])}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => doSendForApproval(sendableApprovalIds(Array.from(selectedIds)))}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Update to Latest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default PayrollRecord;
